import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ComponentProps, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import ErrorSummary from '../../components/ErrorSummary/ErrorSummary';
import Checkbox from '../../components/form/Checkbox';
import ErrorMessage from '../../components/form/ErrorMessage';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { P } from '../../components/ui';
import config from '../../config';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { submitDeclaration } from '../../service/form';
import { Query } from '../../types';
import type { ApiError } from '../../utils/api-error';
import { FormExpiredError } from '../../utils/form-expired-error';
import { resolveSelectedFormId, setSelectedFormIdInSession } from '../../utils/form-id';
import { FormSubmittedError } from '../../utils/form-submitted-error';
import { getErrorMessage } from '../../utils/form-utils';
import {
  getEnabledSubsections,
  getOverviewStatus,
  getSubmitGuardViolations,
  hasIncompleteRequiredSubsections,
} from '../../utils/config-utils';
import { parseOptionalBoolValue } from '../../utils/admin-preview';

const title = 'Sign your declaration';
const subTitle = 'Declaration';
const props: Partial<ComponentProps<typeof PageTemplate>> = {
  title,
  subTitle,
  breadcrumbs: { sectionName: subTitle },
};

function Declaration() {
  const id = useId();
  const { fetchJWT } = useAuthContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const inputId = `${id}declare`;
  const { tenant, formConfig } = useFormConfig();
  const { data, refetch } = useFormData();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const isTestSubmissionMode =
    parseOptionalBoolValue(new URLSearchParams(location.search || '').get('test')) === true;
  const buildSummaryPath = (formId?: string) => {
    const searchParams = new URLSearchParams(location.search || '');
    if (formId) {
      searchParams.set('formId', formId);
    }
    return `/${tenant}/${config.urls.summary}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  };

  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = useForm<{
    declare: string;
  }>({
    ...config.form,
    defaultValues: { declare: '' },
  });

  const { mutate } = useMutation<{ submittedAt?: string; formId?: string } | boolean, ApiError>({
    mutationFn: async () => {
      try {
        const { idToken } = await fetchJWT();
        if (!idToken) {
          throw Error('Missing accessToken');
        }
        return await submitDeclaration(
          idToken,
          selectedFormId ? { form_id: selectedFormId } : undefined,
        );
      } catch (e) {
        if (e instanceof FormExpiredError || e instanceof FormSubmittedError) {
          navigate(`/${tenant}/${config.urls.landingAccounts}`);
          return false;
        }
        throw e;
      }
    },
    onSuccess: async (result) => {
      if (result) {
        const submittedAt =
          typeof result === 'object' && result ? (result as any)?.submittedAt : undefined;
        const formId = typeof result === 'object' && result ? (result as any)?.formId : undefined;
        const resolvedFormId = formId || selectedFormId;

        if (resolvedFormId) {
          setSelectedFormIdInSession(tenant, resolvedFormId);
        }

        if (submittedAt && resolvedFormId && typeof window !== 'undefined') {
          try {
            window.sessionStorage.setItem(`submittedAt-${resolvedFormId}`, submittedAt);
          } catch {
            // ignore storage errors
          }
        }

        await queryClient.invalidateQueries({
          queryKey: [Query.FormData],
        });

        navigate(buildSummaryPath(resolvedFormId));
      }
    },
    onError: (err) => setError('declare', { message: err.message }),
  });

  return (
    <PageTemplate {...props}>
      <ErrorSummary errors={errors} idPrefix={id} />

      <P>
        By submitting your questionnaire you confirm that the information you are providing is
        correct to the best of your knowledge
      </P>

      <form
        onSubmit={handleSubmit(async () => {
          if (!isTestSubmissionMode) {
            // Validate submit guards against the latest server state to avoid stale/optimistic answers.
            const latest = (await refetch())?.data || data || { answers: {} };
            const latestEnabledSubsections = getEnabledSubsections(formConfig, latest);
            if (
              tenant === 'scs' &&
              hasIncompleteRequiredSubsections(
                formConfig.sections,
                getOverviewStatus(formConfig.sections, latestEnabledSubsections, latest),
              )
            ) {
              setError('declare', {
                message: 'Complete Q4 and every started quarter before submitting this form',
              });
              return;
            }
            const submitGuardViolations = getSubmitGuardViolations(
              formConfig.sections || [],
              latestEnabledSubsections,
              latest,
            );
            if (submitGuardViolations.length > 0) {
              setError('declare', { message: submitGuardViolations[0].message });
              return;
            }
          }
          await mutate();
        })}
      >
        <div className={`govuk-form-group ${errors.declare && 'govuk-form-group--error'}`}>
          {errors.declare && <ErrorMessage error={errors.declare} />}

          <div className="govuk-checkboxes__item">
            <Checkbox
              {...register('declare', {
                required: {
                  value: true,
                  message: getErrorMessage('required', 'Declaration', ''),
                },
              })}
              id={inputId}
            />

            <label className="govuk-label govuk-checkboxes__label" htmlFor={inputId}>
              I agree
            </label>
          </div>
        </div>

        <button
          type="submit"
          className="govuk-button"
          data-module="govuk-button"
          data-prevent-double-click="true"
        >
          Submit your form
        </button>
      </form>
    </PageTemplate>
  );
}

export default Declaration;
